import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { StatusCodes } from 'http-status-codes';
import { logInterceptedError } from 'shared/hooks/useInitializeDataDogLogs';
import { redirectToLogin, refreshToken } from 'reduxStore/user/utils';

type RetriableConfig = InternalAxiosRequestConfig & { _retry?: boolean };

/**
 * Response-interceptor helper for axios clients that authenticate via a bearer
 * token derived from the `wbAuthToken` cookie. On 401, refreshes the token
 * (`refreshToken` shares one in-flight call across all callers) and replays
 * the request once with a fresh `Authorization` header. On refresh failure,
 * navigates the user to the login page via `redirectToLogin`.
 *
 * Intended for AI clients only (`aiSummaryClient`, `aiAvatarClient`). Do not
 * apply to `authClient` — `refreshToken()` is dispatched on it.
 *
 * @internal — used only by the AI axios clients in this directory.
 */
export const retryWithRefreshOn401 = async (
  client: AxiosInstance,
  error: unknown
): Promise<unknown> => {
  if (!axios.isAxiosError(error)) {
    throw error;
  }

  const originalRequest = error.config as RetriableConfig | undefined;

  if (
    error.response?.status !== StatusCodes.UNAUTHORIZED ||
    !originalRequest ||
    originalRequest._retry
  ) {
    throw error;
  }

  try {
    await refreshToken();
  } catch (refreshError) {
    redirectToLogin();
    throw refreshError;
  }

  const retryConfig: RetriableConfig = { ...originalRequest, _retry: true };
  return client(retryConfig);
};

/**
 * Wires {@link retryWithRefreshOn401} as a response interceptor on `client`,
 * adding logging + post-failure handling.
 *
 * - When the retry succeeds, the replayed response is returned to the caller.
 * - When the retry path throws, the error is logged via `logInterceptedError`.
 * - With `swallowFinalError: true`, the rejection is converted to a resolved
 *   `undefined` (preserves the long-standing `aiAvatarClient` contract).
 *   Otherwise the rejection is propagated to the caller.
 *
 * Returns the axios interceptor ID so callers can later detach it via
 * `client.interceptors.response.eject(id)`.
 *
 * @internal — used only by the AI axios clients in this directory.
 */
export const attachRetryWithRefreshOn401Interceptor = (
  client: AxiosInstance,
  options: { swallowFinalError?: boolean } = {}
): number =>
  client.interceptors.response.use(
    (response) => response,
    async (error: unknown) => {
      try {
        return await retryWithRefreshOn401(client, error);
      } catch (finalError) {
        logInterceptedError(finalError);
        if (options.swallowFinalError) {
          return undefined;
        }
        return Promise.reject(finalError);
      }
    }
  );
